// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Package entrypoint.
 * @author James Reid
 */

// @ts-check

// @@no-imports

// @@body
/**
 * @todo Allow items to be removed from calculated BOM.
 * @todo Add lead alternative flashing to calculator.
 * @todo Add spray contact bonding as upgrade option for one piece roofs (will
 * require re-adding sku, and updating primer bom resolver).
 * @todo Implement top and breather drain extras.
 * @todo Implement pipe box pocket pipe seal as optional way of sealing a pipe.
 * @todo Add calculator bom map version and categorised version.
 * @todo Change double linked list node typedef to "doubly" linked node.
 * @todo Implement add rectangle method on rectilinear polygon to allow clients
 * to join deck zones into larger zones without a seam.
 * @todo Implement adding split lines to calculator, which will be used to
 * divide copy of internal polygon *before* then creating all of the deck zones.
 * @todo Implement offcut tracker so that trims can be sourced from offcuts of
 * previous edge trims if possible.
 * @todo Get joiners added to website so that when an offcut is used, an
 * additional joiner for trim can be added.
 * @todo Check import orders, especially in products module.
 * @todo Update membrane skus in skus file, remove fleece backed primer and
 * contact bonding, and update flat skus array export.
 * @todo Add pipe drain option using uncured flashing tape patches (see training
 * hub on r4r https://www.rubber4roofs.co.uk/how-to-waterproof-seal-a-pipe-on-a-rubber-roof).
 * @todo Consider adding option for "T" patches at each end of a seam join (see
 * training hub on r4r https://www.rubber4roofs.co.uk/how-to-join-two-pieces-of-epdm).
 * @todo Correctly implement mechanical fixing tape according to tutorial on r4r
 * training hub (see https://www.rubber4roofs.co.uk/why-do-i-need-to-install-russ-mechanical-fixings).
 * See rrd products below which are required for adding mechanical fixing (RUSS)
 * tape excluding primer:
 * - https://www.rubberroofingdirect.co.uk/6-russ-tape.html
 * - https://www.rubberroofingdirect.co.uk/2-50mm-seam-plates.
 * @todo Add patch sealant around all cured/uncured tapes as an upgrade option,
 * allowing a choice of sealant between lapEdge and msPolymer.
 * @todo Add display info to membrane type and enums.
 * @todo Check all different membrane options wrt dividing the polygon in
 * different directions etc..
 * @todo Account for "corners" where both edge types are the same along a
 * straight edge by dissolving vertices with equal edge types before running
 * corner calculator.
 * @todo Account for seam direction with considering gutter edges/fall of roof.
 * @todo Add check on polygon, rectilinear polygon, and rectangle classes that
 * edges and vertices match in static from method.
 * @todo Add check on rectangle that the edges and vertices passed form a
 * rectangle.
 * @todo Add default rotation to rectangle class from method inferred from
 * elevation of rectangle against grid (infer rotation if not passed).
 * @todo Add errors on mutation methods when the "isLocked" property is set.
 * Additionally add an isLocked override property to appropriate "external"
 * mutation methods such as translate and rotate, which do not change geometry
 * of shape (alternatively allow these methods by default). Is locked property
 * should prevent adding or removing vertices or splitting edges.
 * @todo Account for additional cross axis seams required at max roll length.
 * @todo Add overrides for all volumes of adhesive which can be modified by the
 * user in the final BOM (volumes of same product should be grouped with a
 * fields for "min recommended volume", "requested volume", and "provided
 * volume" given calculated BOM).
 * @todo Add option to include client existing stock of adhesive etc. to
 * discount from required quantities.
 * @todo Consider typescript transition with tsdoc and typedoc:
 * - https://tsdoc.org/
 * - https://tsdoc.org/pages/packages/eslint-plugin-tsdoc/
 * - https://typedoc.org/
 * - https://www.npmjs.com/package/typedoc
 * - https://www.npmjs.com/package/@microsoft/tsdoc
 * - https://khalilstemmler.com/blogs/typescript/eslint-for-typescript/.
 * @todo Add detail class such that each deck just has an array of details of
 * different types, each of which have their own serialization method.
 * @todo Change LineItem price to be subtotal and vatSubtotal respectively.
 * @todo Remove presets directory (products no longer required, only keep copy
 * of preset stock in repo admin directory). Deck presets should be moved to web
 * scripts, config should be moved to own directory/file.
 * @todo Change to new internal calculator skus, and remove regexp and sku files
 * from stock directory.
 * @todo Update products to follow data received from new backend particularly
 * with respect to dimensions.
 * @todo Consider splitting calculator package out into independent repo for
 * EPDM calculator, and then create multiple additional repos with calculator
 * logic for the many different flat roof types. Otherwise work out a way to
 * refactor/reorganise repo to allow space for different calculators.
 * Alternatively refactor existing calculator to be system-flexible, and add
 * behaviours in each sub calculator which respond to the type of roof/system
 * being specified.
 * @todo Add ability to add ridge line features to decks for pitched roofs.
 * @todo Remove decorated error from bit buffer implementation and from anywhere
 * else in package.
 * @todo Refactor adhesiveCoverages in config to be a more generic coverages
 * property for future flexibility.
 * @todo Refactor (adhesive)coverages property in config to be flat with no
 * nesting (i.e.) coverages just contains a named set of coverage objects.
 * @todo Refactor client limits to be dimension limits in config, which should
 * be a flat object of named dimension limits set by client. A dimension limit
 * will refer to a specific product eg membrane etc., and will itself contain
 * a limit for each available dimension. Remove waste limits from calculator,
 * favouring just specifying the cheapest/least wasteful solution in every case.
 * If waste limits are kept, they should not be modifiable; they can be config
 * values, but not serializable or modifiable.
 * @todo Refactor membrane/epdm specific configs (membrane, seam direction,
 * rubber join, contact border depth, tapes, overhangs) under an epdm property
 * in config. Each system can then have custom areas like this under config.
 * @todo Refactor parapet to defaultParapet, and move under new deck property in
 * config. Include substrate and trims as a property nested under deck property
 * too.
 * @todo Refactor extra allowances to coverageExcessFactors, and generalise
 * the properties not by product but by type (i.e. coverage based products and
 * linear based products).
 * @todo Refactor config property order to reflect order in serialization.
 * @todo Implement deck detail serialization on a per-feature basis, serializing
 * all features/details and appending to deck serialization which "!" char
 * separation.
 * @todo Other flat roof systems to include (many brands/systems under each roof
 * type):
 * - EPDM.
 * - GRP/fibreglass.
 * - PVC.
 * - Bitumen felt (BUR/torch-on).
 * - Mastic asphalt.
 * - PU liquid membrane.
 * - Green roof (commonly with EPDM roof underneath for waterproofing).
 * @todo Remove *all* index exports of types in order to prevent circular
 * dependencies, or otherwise work out a different way of structuring certain
 * data etc. in order to prevent circular dependencies whilst still maintaining
 * a barrel export of types.
 * @todo Add support for inferring product details from details returned from
 * api instead of from sku code.
 * @todo Change calculator bom method naming to have a function to update bom
 * whenever config is changed, and a bom getter which returns currently stored
 * bom value such that the bom is calculated less frequently for no good reason.
 * @todo Protect config on calculator with setter (i.e. method to update config
 * which also automatically updates config).
 * @todo Change "extraAllowances" on config to "surplusFactor" or similar.
 * @todo Convert Stock and config object to singletons (convert config object to
 * a class) such that they do not need to be passed to the every calculator, and
 * can instead be fetched from the global singleton instance on a base
 * calculator class.
 * @todo Implement error handling system on calculators with a common
 * "StockError" and/or "CalculatorError" class which details what calculation
 * error occurred, what item(s) are out of stock.
 * @todo Add an ignore stock option to URLs which fetches stock from calculator
 * irrelevant of current stock.
 */

// @@exports
export * from "./calculators/index.js"
export * from "./presets/index.js"
export * from "./products/index.js"
export * from "./stock/index.js"
export * from "./types/index.js"
export * from "./utils/index.js"
export * from "./Calculator.js"
export * from "./Deck.js"
